// src/services/messageTemplates.js
// 숙소 운영 단계별 메시지 템플릿

// ─── 날짜 → 한국어 포맷 ───
export function formatKoreanDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return `${d.getMonth() + 1}월 ${d.getDate()}일`;
}

// ─── 기본 템플릿 문자열 ───
// 변수: {이름} {객실명} {입실시간} {퇴실시간} {안내사항} {체크인날짜} {체크아웃날짜} {문의전화}
export const DEFAULT_TEMPLATE_STRINGS = {

  reservation:
`안녕하세요 {이름} 고객님 🙂

{객실명} 예약이 확인되었습니다.

📅 체크인: {체크인날짜} {입실시간}
📅 체크아웃: {체크아웃날짜} {퇴실시간}

궁금한 점은 언제든지 연락 주세요.
즐거운 여행 준비 되세요 ✈️`,

  checkin:
`안녕하세요 {이름} 고객님 🙂

오늘 입실하실 {객실명} 안내 드립니다.

⏰ 입실 시간: {입실시간}
🔑 도어락 비밀번호는 도착 시 안내드립니다
🚗 주차: 건물 앞 주차 가능
{안내사항}
도착하시면 편하게 연락 주세요!`,

  mannertime:
`안녕하세요 {이름} 고객님 🙂

편안한 투숙 되고 계신가요?

🔔 매너타임 안내
밤 10시 이후에는 음악, 대화 소리를
조금만 줄여주시면 감사하겠습니다.

주변 이웃분들을 위해 협조 부탁드립니다 🙏
나머지 일정도 즐겁게 보내세요!`,

  checkout:
`안녕하세요 {이름} 고객님 🙂

오늘 퇴실 안내 드립니다.

⏰ 퇴실 시간: {퇴실시간}
🧹 퇴실 전 부탁드립니다
  · 음식물 쓰레기 봉투에 담아주세요
  · 창문·도어락 확인해 주세요

이용해주셔서 진심으로 감사합니다 😊
다음에 또 뵙겠습니다!`,

  payment:
`안녕하세요 {이름}님 😊
{숙소명}입니다

입금이 확인되어 예약이 확정되었습니다.

📅 체크인: {체크인날짜}
🏡 객실: {객실명}

이용일에 뵙겠습니다 🙏
문의: {문의전화}`,

  review:
`안녕하세요 {이름} 고객님 🙂

저희 {객실명} 이용해주셔서 감사합니다.

혹시 불편하신 점은 없으셨나요?
소중한 후기를 남겨주시면 큰 힘이 됩니다 ⭐

짧은 한 줄도 괜찮습니다.
좋은 하루 보내세요 🙏`,
};

// ─── 변수 치환 렌더러 ───
export function renderTemplate(templateStr, room, data) {
  const notes = room?.notes?.trim() || '';
  return templateStr
    .replace(/{이름}/g, data?.name || '고객')
    .replace(/{객실명}/g, room?.name || '예약하신 객실')
    .replace(/{숙소명}/g, data?.pensionName || '저희 숙소')
    .replace(/{입실시간}/g, room?.checkinTime || '15:00')
    .replace(/{퇴실시간}/g, room?.checkoutTime || '11:00')
    .replace(/{안내사항}/g, notes ? notes + '\n' : '')
    .replace(/{체크인날짜}/g, formatKoreanDate(data?.checkin) || '체크인 당일')
    .replace(/{체크아웃날짜}/g, formatKoreanDate(data?.checkout) || '체크아웃 당일')
    .replace(/{문의전화}/g, data?.pensionPhone || '문의 전화')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// ─── 운영 메시지 템플릿 (발송 탭 전용) ───
// 숙소 운영 흐름 순서: 예약 확인 → 입금 확인 → 체크인 → 매너타임 → 체크아웃 → 리뷰 요청
export const MESSAGE_TEMPLATES = {
  reservation: {
    id: 'reservation',
    name: '예약 확인',
    icon: '📋',
    desc: '예약 접수 후 확인 메시지',
    build: (room, data) => renderTemplate(DEFAULT_TEMPLATE_STRINGS.reservation, room, data),
  },

  payment: {
    id: 'payment',
    name: '입금 확인',
    icon: '💰',
    desc: '입금 완료 후 예약 확정 메시지',
    build: (room, data) => renderTemplate(DEFAULT_TEMPLATE_STRINGS.payment, room, data),
  },

  checkin: {
    id: 'checkin',
    name: '체크인 안내',
    icon: '🏠',
    desc: '입실 당일 안내 메시지',
    build: (room, data) => renderTemplate(DEFAULT_TEMPLATE_STRINGS.checkin, room, data),
  },

  mannertime: {
    id: 'mannertime',
    name: '매너타임 안내',
    icon: '🔔',
    desc: '소음·규칙 안내 메시지',
    build: (room, data) => renderTemplate(DEFAULT_TEMPLATE_STRINGS.mannertime, room, data),
  },

  checkout: {
    id: 'checkout',
    name: '체크아웃 안내',
    icon: '👋',
    desc: '퇴실 당일 안내 메시지',
    build: (room, data) => renderTemplate(DEFAULT_TEMPLATE_STRINGS.checkout, room, data),
  },

  review: {
    id: 'review',
    name: '리뷰 요청',
    icon: '⭐',
    desc: '투숙 후 후기 요청 메시지',
    build: (room, data) => renderTemplate(DEFAULT_TEMPLATE_STRINGS.review, room, data),
  },
};

// 발송 탭에 표시할 순서 (운영 흐름 순)
export const OPERATION_TEMPLATE_IDS = ['reservation', 'payment', 'checkin', 'mannertime', 'checkout', 'review'];

// 공통 템플릿 (숙소 전체 공유 — 예약확인·입금확인·리뷰요청)
export const COMMON_TEMPLATE_IDS = ['reservation', 'payment', 'review'];

// 객실별 템플릿 (객실마다 다를 수 있음 — 체크인·매너타임·체크아웃)
export const ROOM_TEMPLATE_IDS = ['checkin', 'mannertime', 'checkout'];

// ─── 메시지 생성 함수 ───
// customTemplate(문자열)이 있으면 우선, rawText가 있으면 그대로 사용
export function buildMessage(templateId, room, data, customTemplate) {
  if (customTemplate) return renderTemplate(customTemplate, room, data);
  const template = MESSAGE_TEMPLATES[templateId];
  if (!template) return '';
  return template.build(room, data);
}

// ─── 솔라피 SMS/LMS 발송 ───
export async function sendMessage({ phone, templateId, room, data, customTemplate, rawText }) {
  const text = rawText ?? buildMessage(templateId, room, data, customTemplate);
  if (!text) throw new Error('메시지 내용이 없습니다');

  const { sendSolapiSMS } = await import('./solapiSms');
  return sendSolapiSMS({ to: phone, text });
}

// ─── 재방문 홍보 템플릿 (마케팅 탭 전용) ───
export const MARKETING_PROMO_TYPES = [
  {
    id: 'earlybird',
    name: '얼리버드 할인',
    icon: '🌅',
    buildMessage: (name) =>
`안녕하세요 ${name} 고객님 😊

저희 펜션을 찾아주셔서 감사합니다.

다음 방문을 미리 예약하시면
얼리버드 특별 할인 혜택을 드립니다!

소중한 인연에 감사드리며,
다시 만날 날을 기다리겠습니다 🙏`,
  },
  {
    id: 'stay',
    name: '숙박 할인',
    icon: '🏷️',
    buildMessage: (name) =>
`안녕하세요 ${name} 고객님 😊

오랫동안 뵙지 못했는데 잘 지내고 계신가요?

재방문 고객님께 특별 숙박 할인 혜택을 드립니다!

편안하고 즐거운 시간 보내러 다시 오세요 🌿`,
  },
  {
    id: 'promo_bbq',
    name: '바비큐 무료',
    icon: '🥩',
    buildMessage: (name) =>
`안녕하세요 ${name} 고객님 😊

재방문 고객님께 특별 혜택을 드립니다!

✅ 바비큐 그릴 무료 이용 이벤트

이번 방문 시 바비큐장을 무료로 이용하세요.
좋은 추억 함께 만들어요 🔥`,
  },
  {
    id: 'firewood',
    name: '장작 무료',
    icon: '🪵',
    buildMessage: (name) =>
`안녕하세요 ${name} 고객님 😊

재방문 고객님께 따뜻한 혜택을 드립니다!

✅ 장작 무료 제공 이벤트

화롯가에서 따뜻한 시간 보내세요 🔥
다시 만날 날을 기다리겠습니다 😊`,
  },
  {
    id: 'breakfast',
    name: '조식 무료',
    icon: '🍳',
    buildMessage: (name) =>
`안녕하세요 ${name} 고객님 😊

재방문 고객님께 특별 혜택을 드립니다!

✅ 조식 무료 제공 이벤트

맛있는 조식과 함께 특별한 아침을 즐겨보세요 🌅
오래간만에 다시 방문해 주세요!`,
  },
  {
    id: 'pool',
    name: '수영장 무료',
    icon: '🏊',
    buildMessage: (name) =>
`안녕하세요 ${name} 고객님 😊

여름 시즌 특별 혜택을 드립니다!

✅ 수영장 무료 이용 이벤트

시원한 여름, 저희 펜션에서 함께 보내요 ☀️
재방문 고객님을 위한 특별 혜택입니다!`,
  },
  {
    id: 'family',
    name: '가족 특가',
    icon: '👨‍👩‍👧',
    buildMessage: (name) =>
`안녕하세요 ${name} 고객님 😊

가족과 함께하는 특별한 시간을 위해
재방문 가족 특가 혜택을 드립니다!

✅ 가족 특가 패키지 이벤트

소중한 가족과 잊지 못할 추억 만들러 오세요 🏡`,
  },
  {
    id: 'group',
    name: '단체 특가',
    icon: '👥',
    buildMessage: (name) =>
`안녕하세요 ${name} 고객님 😊

단체 모임을 위한 특별 혜택을 드립니다!

✅ 단체 특가 이벤트

즐거운 MT, 워크숍, 모임을 저희 펜션에서 함께하세요 🎉
다시 만나뵐 날을 기다리겠습니다!`,
  },
];

// ─── 재방문 홍보 발송 (마케팅 탭 전용) ───
export async function sendMarketingMessage({ phone, messageText }) {
  const { sendSolapiSMS } = await import('./solapiSms');
  return sendSolapiSMS({ to: phone, text: messageText });
}

// ─── 파싱 성공 기준 ───
export function isParseSuccess(parsed) {
  let count = 0;
  if (parsed.phone) count++;
  if (parsed.checkin) count++;
  if (parsed.name) count++;
  return count >= 2;
}
